import { createSelector } from 'reselect';
import { getCurrentSeason } from './seasonSelectors';
import { getViewIndex } from './commonSelectors';
import subLayerGetters from './subLayerGetters';
import { isLayerSeasonAgnostic } from '../utils/layerData';
import { SEASON_AGNOSTIC } from '../constants/seasonIds';
import { getCurrentLayerId } from './layerSelectorsNew';

export const getLayerList = (state) => state.layers.list;

export const getCurrentLayer = createSelector(
  [getCurrentLayerId, getCurrentSeason, getLayerList],
  (currentLayerId, currentSeasonId, layerList) => {
    if (isLayerSeasonAgnostic(currentLayerId)) {
      return layerList[SEASON_AGNOSTIC]?.[currentLayerId];
    }
    return layerList[currentSeasonId]?.[currentLayerId];
  }
);

export const getAvailableLayerIdsForSeasonsFunc = (layerList) =>
  Object.keys(layerList).reduce(
    (_layerIds, seasonId) => ({
      ..._layerIds,
      [seasonId]: layerList[seasonId].layerIds || [],
    }),
    {}
  );

export const getAvailableLayerIdsForSeasons = createSelector(
  [getLayerList],
  getAvailableLayerIdsForSeasonsFunc
);

// If called for layer type that doesn't have sublayers, will safely return null.
export const getActiveSubLayer = createSelector(
  [getCurrentLayerId, getCurrentLayer, getViewIndex],
  (currentLayerId, currentLayer, index) => {
    if (!currentLayer || !currentLayer.activeSubLayerIds) {
      return null;
    }
    const activeSubLayerId = currentLayer.activeSubLayerIds[index];

    const subLayerGetter = subLayerGetters[currentLayerId];
    return (
      (subLayerGetter && subLayerGetter(currentLayer, activeSubLayerId)) ||
      (currentLayer.subLayers && currentLayer.subLayers[activeSubLayerId])
    );
  }
);

export const getActiveSubLayerId = createSelector(
  [getCurrentLayer, getViewIndex],
  (currentLayer, index) => {
    if (!currentLayer || !currentLayer.activeSubLayerIds) {
      return null;
    }
    return currentLayer.activeSubLayerIds[index];
  }
);

export const getCurrentLegend = createSelector(
  [getCurrentLayer, getActiveSubLayer],
  (layer, subLayer) => {
    if (layer && layer.legend) {
      return layer.legend;
    }
    // If the layer doesn't have a legend, then layer may be a type with
    // sublayers, e.g. Applications.
    return subLayer && subLayer.legend;
  }
);

export const getFvpCacheBuster = createSelector(
  [getCurrentLegend],
  (legend) => {
    if (!legend) {
      return null;
    }

    const legendLength = legend.length;
    const firstInterval = legend[0];
    const firstValue =
      firstInterval.value !== undefined
        ? firstInterval.value
        : firstInterval.stringValue;
    const lastInterval = legend[legendLength - 1];
    const lastValue =
      lastInterval.value !== undefined
        ? lastInterval.value
        : lastInterval.stringValue;
    return `${firstValue}_${lastValue}_${legendLength}`;
  }
);
